// Shapes the page sends to the browser. Plain objects, so they cross the server/client border unchanged.
import type { Agreement, CriterionStatus, FeatureStatus, LinkKind, Priority, QuestionStatus } from "./db/schema.ts";

export type { Agreement, CriterionStatus, FeatureStatus, LinkKind, Priority, QuestionStatus };

/** "goal" etc. plus the question. */
export type Kind = LinkKind | "question";

export const PREFIX: Record<Kind, string> = {
  goal: "Z",
  measure: "M",
  requirement: "A",
  criterion: "K",
  feature: "F",
  question: "Q",
};

interface Base {
  id: number;
  num: number;
  title: string;
  description: string | null;
}

export interface Goal extends Base {
  status: Agreement;
}
/** Messgröße: how a goal's success is measured. */
export interface Measure extends Base {
  goalId: number;
  status: Agreement;
}
export interface Requirement extends Base {
  priority: Priority;
  /** Main requirement (Bereich) this item belongs to; null for a main requirement itself. */
  parentId: number | null;
}
export interface Criterion extends Base {
  requirementId: number;
  status: CriterionStatus;
}
export interface Feature extends Base {
  status: FeatureStatus;
}
export interface Baustein {
  id: number;
  featureId: number;
  title: string;
}
export interface Question extends Base {
  topic: string | null;
  answer: string | null;
  status: QuestionStatus;
  important: boolean;
  /** Only the links stored on this question; a link from another question shows up in the graph. */
  links: { kind: Kind; targetId: number }[];
}

export interface Board {
  project: { id: number; name: string; client: string | null };
  goals: Goal[];
  measures: Measure[];
  requirements: Requirement[];
  criteria: Criterion[];
  features: Feature[];
  bausteine: Baustein[];
  /** [goalId, requirementId] */
  goalRequirements: [number, number][];
  /** [featureId, requirementId] */
  featureRequirements: [number, number][];
  questions: Question[];
}

export const NO_AREA = "Ohne Thema";

/** Entries grouped by a text field (questions by topic), groups in order of their first entry. */
export function byArea<T extends { area: string | null }>(list: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const r of list) {
    const a = r.area?.trim() || NO_AREA;
    groups.set(a, [...(groups.get(a) ?? []), r]);
  }
  return [...groups];
}
